import {
  hasJustificationForOverride,
  needsJustification,
  type OverrideJustification,
  type OverrideValue,
} from 'features/libra/overrides/overrideJustification'
import { GeckoMeasurementUnit } from 'types'

import { getCalculatedStrategyType, getCalculatedZoneArea } from './niiStrategy'
import type { ZoneParams } from './zoneAnalysisApi'

/** Where an overridable zone value, its calculation and its justification live. */
export interface OverridableZoneField {
  label: string
  getOverrideValue: (zone: ZoneParams) => OverrideValue | undefined
  getCalculatedValue: (zone: ZoneParams) => OverrideValue | undefined
  getJustification: (zone: ZoneParams) => OverrideJustification | undefined
  /** The stored unit of a numeric value, for display in the preferred unit. */
  unit?: GeckoMeasurementUnit
  /** Display text in the stored unit, for where no preferred unit applies. */
  formatStoredValue?: (value: OverrideValue) => string
}

export const SURFACE_AREA_FIELD: OverridableZoneField = {
  label: 'Total surface area',
  getOverrideValue: (zone) => zone.total_surface_area_sq_m_override,
  getCalculatedValue: getCalculatedZoneArea,
  getJustification: (zone) =>
    zone.total_surface_area_sq_m_override_justification,
  unit: GeckoMeasurementUnit.SquareMeters,
  formatStoredValue: (value) =>
    typeof value === 'number' ? `${value.toFixed(2)} m²` : value,
}

export const STRATEGY_FIELD: OverridableZoneField = {
  label: 'NII strategy',
  getOverrideValue: (zone) => zone.nii_strategy?.strategy_type_override,
  getCalculatedValue: (zone) => getCalculatedStrategyType(zone.nii_strategy),
  getJustification: (zone) =>
    zone.nii_strategy?.strategy_type_override_justification,
  formatStoredValue: (value) => `Type ${value}`,
}

/** Every overridable zone field; add a new one here. */
export const OVERRIDABLE_ZONE_FIELDS: ReadonlyArray<OverridableZoneField> = [
  SURFACE_AREA_FIELD,
  STRATEGY_FIELD,
]

export interface ZoneOverride {
  field: OverridableZoneField
  overrideValue: OverrideValue
  calculatedValue: OverrideValue | undefined
  /** Set only when given for the current override value. */
  justification: OverrideJustification | undefined
}

/**
 * The zone's overrides that replace a calculated value or carry a
 * justification. A value typed in with nothing calculated is plain entry.
 */
export function listZoneOverrides(
  zone: ZoneParams
): ReadonlyArray<ZoneOverride> {
  return OVERRIDABLE_ZONE_FIELDS.flatMap((field) => {
    const overrideValue = field.getOverrideValue(zone)
    if (overrideValue === undefined) return []
    const calculatedValue = field.getCalculatedValue(zone)
    const saved = field.getJustification(zone)
    const justification = hasJustificationForOverride(saved, overrideValue)
      ? saved
      : undefined
    return calculatedValue === undefined && !justification
      ? []
      : [{ field, overrideValue, calculatedValue, justification }]
  })
}

/** Display text in the stored unit, ignoring the user's preferred units. */
export function formatStoredZoneOverrideValue(
  field: OverridableZoneField,
  value: OverrideValue
): string {
  return field.formatStoredValue?.(value) ?? String(value)
}

/**
 * "<field> (<zone>)" for each override still needing a justification.
 * Unreviewed AI drafts are skipped: they are never saved.
 */
export function listUnjustifiedOverrideLabels(
  zones: ReadonlyArray<ZoneParams>
): ReadonlyArray<string> {
  return zones
    .filter((zone) => !zone.ai_suggested)
    .flatMap((zone) =>
      OVERRIDABLE_ZONE_FIELDS.filter((field) =>
        needsJustification(
          field.getOverrideValue(zone),
          field.getCalculatedValue(zone),
          field.getJustification(zone)
        )
      ).map((field) => `${field.label} (${zone.title})`)
    )
}
